import { CodeBlock } from "../components/CodeBlock";
import { useMetaTags } from "../components/MetaTags";

function Tool({
  name,
  description,
  params,
}: {
  name: string;
  description: string;
  params?: { name: string; type: string; required?: boolean; description: string }[];
}) {
  return (
    <div className="border border-slate-200 rounded-xl p-5 space-y-3">
      <div className="flex items-center gap-3">
        <code className="text-sm font-mono font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
          {name}
        </code>
      </div>
      <p className="text-sm text-slate-600">{description}</p>
      {params && params.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-1.5 pr-3 font-medium text-slate-700">Parameter</th>
                <th className="text-left py-1.5 pr-3 font-medium text-slate-700">Type</th>
                <th className="text-left py-1.5 font-medium text-slate-700">Description</th>
              </tr>
            </thead>
            <tbody className="text-slate-500">
              {params.map((p) => (
                <tr key={p.name} className="border-b border-slate-100">
                  <td className="py-1.5 pr-3 font-mono text-xs">
                    {p.name}
                    {p.required && <span className="text-red-400 ml-0.5">*</span>}
                  </td>
                  <td className="py-1.5 pr-3 text-xs">{p.type}</td>
                  <td className="py-1.5 text-xs">{p.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function DocsMcp() {
  useMetaTags({
    title: "OmniCanvas MCP Server — Connect Your Notes to AI",
    description:
      "Connect OmniCanvas to Claude and other AI assistants via the Model Context Protocol (MCP). Tools, setup, and capabilities for querying your spatial notes.",
    url: "https://omnicanvasnotes.com/docs/mcp",
  });
  return (
    <div className="pt-28 pb-20">
      <div className="max-w-3xl mx-auto px-6">
        <p className="text-sm text-slate-400 mb-2">Last updated: March 7, 2026</p>
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 mb-4">
          MCP Server
        </h1>
        <p className="text-lg text-slate-600 mb-12">
          Connect OmniCanvas to AI assistants like Claude Desktop, Cursor, Windsurf, and any{" "}
          <a
            href="https://modelcontextprotocol.io"
            className="text-indigo-600 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            MCP-compatible
          </a>{" "}
          client. Available on the{" "}
          <a href="/pricing" className="text-indigo-600 hover:underline">
            Pro and Team plans
          </a>
          .
        </p>

        <div className="prose prose-slate max-w-none space-y-10 text-slate-600 leading-relaxed">
          {/* What is MCP */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">What is MCP?</h2>
            <p>
              The <strong>Model Context Protocol</strong> is an open standard that lets AI assistants
              securely access your tools and data. With OmniCanvas's MCP server, your AI assistant
              can read, create, search, and organize your notes — all with your explicit permission
              on every action.
            </p>
          </section>

          {/* Setup */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Setup</h2>
            <ol className="list-decimal pl-5 space-y-3 text-sm">
              <li>
                Open OmniCanvas and go to <strong>Settings → API Access</strong>
              </li>
              <li>
                Click <strong>Create</strong> to generate an API key
              </li>
              <li>
                Copy the MCP configuration that appears (it includes your key)
              </li>
              <li>Paste it into your AI client's MCP settings file</li>
            </ol>

            <p className="mt-4 text-sm">
              Or configure it manually — add this to your client's MCP config:
            </p>

            <CodeBlock label="MCP Configuration">{`{
  "mcpServers": {
    "omnicanvas": {
      "url": "https://app.omnicanvasnotes.com/api/v1/mcp",
      "headers": {
        "Authorization": "Bearer pk_live_your_key_here"
      }
    }
  }
}`}</CodeBlock>
          </section>

          {/* Client-specific setup */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Client-Specific Setup</h2>

            <h3 className="text-lg font-bold text-slate-800 mt-6 mb-2">Claude Desktop</h3>
            <p className="text-sm">
              Open <strong>Settings → Developer → Edit Config</strong> and paste the configuration
              above into your <code className="text-slate-800 bg-slate-100 px-1 rounded">claude_desktop_config.json</code>.
              Restart Claude Desktop.
            </p>

            <h3 className="text-lg font-bold text-slate-800 mt-6 mb-2">Claude Code (CLI)</h3>
            <p className="text-sm">
              Add to your project's <code className="text-slate-800 bg-slate-100 px-1 rounded">.mcp.json</code> or
              global <code className="text-slate-800 bg-slate-100 px-1 rounded">~/.claude/settings.json</code> under
              the <code className="text-slate-800 bg-slate-100 px-1 rounded">mcpServers</code> key.
            </p>

            <h3 className="text-lg font-bold text-slate-800 mt-6 mb-2">Cursor</h3>
            <p className="text-sm">
              Go to <strong>Settings → MCP</strong> and add a new server. Use the URL and
              Authorization header from the config above.
            </p>

            <h3 className="text-lg font-bold text-slate-800 mt-6 mb-2">Other Clients</h3>
            <p className="text-sm">
              Any client that supports the{" "}
              <a
                href="https://modelcontextprotocol.io/specification/2025-03-26"
                className="text-indigo-600 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                MCP 2025-03-26 spec
              </a>{" "}
              with Streamable HTTP transport will work. Point it
              at <code className="text-slate-800 bg-slate-100 px-1 rounded">https://app.omnicanvasnotes.com/api/v1/mcp</code> with
              your API key as a Bearer token.
            </p>
          </section>

          {/* Available Tools */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Available Tools</h2>
            <p className="mb-4">
              Your AI assistant can use 14 tools to interact with your notes. Here's what each one does:
            </p>
            <div className="space-y-4">
              <Tool
                name="list_notes"
                description="List your notes with optional filtering by tag, folder, or search query."
                params={[
                  { name: "tag", type: "string", description: "Filter by tag" },
                  { name: "folderId", type: "string", description: "Filter by folder" },
                  { name: "search", type: "string", description: "Search titles and OCR text" },
                  { name: "limit", type: "integer", description: "Max results (1–100, default 50)" },
                  { name: "offset", type: "integer", description: "Skip N results" },
                ]}
              />

              <Tool
                name="get_note"
                description="Get a note by ID with full content including canvas data, tags, and folders."
                params={[
                  { name: "id", type: "string", required: true, description: "Note ID" },
                  { name: "includeCanvas", type: "boolean", description: "Include canvas data (default true)" },
                ]}
              />

              <Tool
                name="create_note"
                description="Create a new note with optional title, tags, folders, and canvas data."
                params={[
                  { name: "title", type: "string", description: "Note title" },
                  { name: "tags", type: "string[]", description: "Tags to apply" },
                  { name: "folderIds", type: "string[]", description: "Folders to add the note to" },
                  { name: "canvasData", type: "object", description: "Excalidraw canvas JSON" },
                ]}
              />

              <Tool
                name="update_note"
                description="Update a note. Only provided fields are changed. Tags and folders replace existing values."
                params={[
                  { name: "id", type: "string", required: true, description: "Note ID" },
                  { name: "title", type: "string", description: "New title" },
                  { name: "tags", type: "string[]", description: "New tags (replaces all)" },
                  { name: "folderIds", type: "string[]", description: "New folders (replaces all)" },
                  { name: "isPinned", type: "boolean", description: "Pin or unpin" },
                ]}
              />

              <Tool
                name="delete_note"
                description="Soft delete a note (moves to trash). Pass permanent=true to permanently delete."
                params={[
                  { name: "id", type: "string", required: true, description: "Note ID" },
                  { name: "permanent", type: "boolean", description: "Permanently delete (default false)" },
                ]}
              />

              <Tool
                name="list_folders"
                description="List all your folders with note counts."
              />

              <Tool
                name="create_folder"
                description="Create a new folder, optionally nested inside another."
                params={[
                  { name: "name", type: "string", required: true, description: "Folder name" },
                  { name: "parentId", type: "string", description: "Parent folder for nesting" },
                ]}
              />

              <Tool
                name="search_notes"
                description="Full-text search across note titles and OCR text. Returns excerpts with matching context."
                params={[
                  { name: "q", type: "string", required: true, description: "Search query (min 2 chars)" },
                  { name: "limit", type: "integer", description: "Max results (default 20)" },
                ]}
              />

              <Tool
                name="list_tags"
                description="List all tags across your notes, sorted by usage frequency."
              />

              <Tool
                name="export_note"
                description="Export a note as full Excalidraw JSON without size limits."
                params={[
                  { name: "id", type: "string", required: true, description: "Note ID" },
                ]}
              />

              <Tool
                name="list_versions"
                description="List version history for a note (12-month retention on paid plans)."
                params={[
                  { name: "noteId", type: "string", required: true, description: "Note ID" },
                ]}
              />

              <Tool
                name="restore_version"
                description="Restore a note to a previous version. Current state is auto-saved first."
                params={[
                  { name: "noteId", type: "string", required: true, description: "Note ID" },
                  { name: "versionId", type: "string", required: true, description: "Version ID to restore" },
                ]}
              />

              <Tool
                name="clip_url"
                description="Clip a web page or YouTube video. Extracts title, content, and optional transcript. YouTube URLs are auto-detected."
                params={[
                  { name: "url", type: "string", required: true, description: "URL to clip" },
                  { name: "mode", type: "string", description: '"visual" (screenshot + text) or "text" (default)' },
                  { name: "includeTranscript", type: "boolean", description: "Fetch YouTube transcript (quota-limited)" },
                ]}
              />

              <Tool
                name="youtube_transcript"
                description="Fetch the transcript for a YouTube video. Quota-limited per month."
                params={[
                  { name: "videoId", type: "string", required: true, description: "YouTube video ID (11 characters)" },
                ]}
              />
            </div>
          </section>

          {/* Resources */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Resources</h2>
            <p className="mb-4">
              MCP resources let your AI assistant read structured data from your account:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-sm">
              <li>
                <code className="text-slate-800 bg-slate-100 px-1 rounded">note://&#123;id&#125;</code> — full note content with metadata
              </li>
              <li>
                <code className="text-slate-800 bg-slate-100 px-1 rounded">folder://&#123;id&#125;</code> — folder details with its note list
              </li>
              <li>
                <code className="text-slate-800 bg-slate-100 px-1 rounded">tags://all</code> — all your tags with usage counts
              </li>
            </ul>
          </section>

          {/* Use Cases */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Example Use Cases</h2>
            <p className="mb-3">Once connected, you can ask your AI assistant things like:</p>
            <ul className="list-disc pl-5 space-y-2 text-sm">
              <li>"Search my notes for anything about project Alpha"</li>
              <li>"Create a new note with today's meeting summary and tag it #meetings"</li>
              <li>"Clip this article and save the key points to my Research folder"</li>
              <li>"What did I write about database migrations last week?"</li>
              <li>"Get the transcript from this YouTube video and add it to my notes"</li>
              <li>"Show me all my notes tagged #ideas and summarize the common themes"</li>
              <li>"Restore my project plan to yesterday's version"</li>
            </ul>
          </section>

          {/* Limits */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Limits</h2>
            <ul className="list-disc pl-5 space-y-2 text-sm">
              <li>
                <strong>Rate limit:</strong> 100 requests per minute
              </li>
              <li>
                <strong>Canvas read size:</strong> Notes larger than 50KB return truncated canvas
                data — use <code className="text-slate-800 bg-slate-100 px-1 rounded">export_note</code> for the full file
              </li>
              <li>
                <strong>Encrypted notes:</strong> Canvas data is returned as null (encryption/decryption happens client-side)
              </li>
              <li>
                <strong>YouTube transcripts:</strong> Included with Pro and Team, subject to the monthly transcription allowance
              </li>
            </ul>
          </section>

          {/* Security */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Security</h2>
            <p>
              Your API key is hashed (SHA-256) before storage — we never store the raw key. Each
              request is authenticated and scoped to your account only. You can revoke keys instantly
              in Settings. The MCP server uses Streamable HTTP transport over HTTPS.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
